import { useSyncExternalStore } from 'react';

/**
 * BRO-1567: whether privacy mode is on, and which single contact is currently revealed.
 *
 * WHY THIS IS A MODULE-LEVEL STORE AND NOT A REACT CONTEXT, which is the obvious answer
 * and is wrong here. Every React island on a page mounts through its own `createRoot`
 * (`resources/js/react.jsx`), so a provider placed at the one shared JSX tree does NOT
 * give the islands one state: each root renders its own provider instance with its own
 * `useState`. Decision 1 - revealing a second contact re-hides the first - would then hold
 * inside an island and not between them, so the live feed could have one contact revealed
 * while the chat panel had another.
 *
 * **That failure is invisible and it fails OPEN**: two contacts are showing, nothing on
 * screen says the guarantee has lapsed, and the demo is the moment you find out. A module
 * is shared by every island in the bundle because there is one module instance, so the
 * guarantee is structural rather than conventional.
 *
 * WHAT LIVES WHERE, and the split is decision 2 and decision 3 rather than convenience:
 *
 * - `enabled` comes from the SERVER SESSION, echoed into a page global by the layout on
 *   every request. It has to survive a reload and a second tab and die at sign-out. Not
 *   localStorage (survives sign-out) and not `window.PagePreferences` (writes a cookie
 *   with a one-year expiry), both rejected on the plan for the same reason.
 * - `revealedClientId` lives ONLY here, in memory. It is gone on the next page load, which
 *   is exactly the auto-re-hide on navigation that decision 2 asks for, with no navigation
 *   listener to get wrong and no timer. Decision 2 rules out a timer explicitly: a contact
 *   that un-reveals itself mid-sentence while he is pointing at it is the worse failure.
 *
 * See specification/BRO-1567/implementation-plan.md.
 */

/**
 * The page global that IS the store. Live: every copy of this module reads and writes it, and
 * that is the whole reason it exists - Vite gives each chunk its own copy of this module, so
 * module-level state is not shared and `window` is.
 */
const GLOBAL_KEY = 'BroadcasterPrivacy';

/**
 * BRO-1567 (Quality's T3): THE RENDER-TIME SEEDS, KEPT SEPARATE AND NAMED AS SEEDS.
 *
 * `generation` and `couldNotConfirm` were on `BroadcasterPrivacy` beside `enabled` and
 * `revealedClientId`, which was misleading in the way that costs an afternoon: two of the four
 * are live and two are a photograph of the moment the page rendered. Nothing updates the
 * seeds afterwards, so anything reading `BroadcasterPrivacy.generation` at runtime gets a
 * number that was true once.
 *
 * They are read ONCE, at module load, into module state that is then kept current. Putting
 * them under a name ending in "Seed" means a reader who finds one cannot mistake it for the
 * live value, which is the whole of the fix: the alternative was making them live, and a
 * second live copy of state this feature already proved cannot be kept in step is the worse
 * answer.
 */
const SEED_KEY = 'BroadcasterPrivacySeed';

/** Fired on `window` whenever the state changes, so every copy of this module hears it. */
const CHANGE_EVENT = 'broadcaster:privacy-changed';

interface PrivacyGlobal {
    enabled?: boolean;
    revealedClientId?: number | null;
    messagesRevealedFor?: number | null;
}

export interface PrivacySnapshot {
    /** Privacy mode itself. Session-scoped, server-owned. */
    enabled: boolean;
    /** The one contact currently shown in full, or null. Never persisted. */
    revealedClientId: number | null;
    /**
     * BRO-1583: the one conversation whose MESSAGE BODIES AND MEDIA are shown, or null.
     *
     * A SECOND AXIS, INDEPENDENT OF revealedClientId, and the independence is the feature.
     * revealedClientId shows a contact's identity: name, number, handle. This shows a
     * conversation's content while the identity stays masked, which is what a presenter
     * demonstrating a live account actually wants: the real flow of a chat, with the
     * customer still anonymous.
     *
     * The two do not clear each other. Revealing a contact to compose a message must not
     * silently re-blur the conversation he is mid-way through showing, and hiding the name
     * again must not either.
     *
     * Never persisted, and re-masked by anything that ends the demo context: toggling
     * privacy mode, leaving the conversation, signing out.
     */
    messagesRevealedFor: number | null;
    /**
     * The server has no answer for this session and this tab is holding its mask anyway.
     *
     * An eviction, a restart, or a deploy running `cache:clear` mid-share. The mask stays -
     * un-masking on an unknown is the failure this feature exists to prevent - but the
     * banner must SAY so, because a tab that is masked for a reason it cannot confirm is not
     * the same promise as one the server is standing behind.
     */
    couldNotConfirm: boolean;
}

/**
 * THE STATE LIVES ON `window`, NOT IN THIS MODULE, AND THAT IS A CORRECTION.
 *
 * This module previously held `enabled` and `revealedClientId` as module-level variables, on
 * the reasoning that a module has one instance per bundle so the state is shared by
 * construction. **That is false here, and it was caught in a browser rather than by any
 * test.** Vite duplicated this module into two chunks - `privacyMask-*.js` and the React
 * entry `react-*.js` - so there were two independent copies of the state. The banner went on
 * saying "privacy mode is on" after its own button had turned it off, and the calendar never
 * re-rendered, because each was talking to a different copy.
 *
 * It is the same failure I argued a React context would have (one state per island, fails
 * OPEN, nothing on screen says so) arriving through a completely different door: bundling
 * rather than rendering. Module identity is a property of how the bundler chose to split the
 * graph, which is not a guarantee anybody wrote down and not one that survives a refactor.
 *
 * `window` has exactly one instance per page by definition. It is also what Blade, Alpine
 * and the FullCalendar path already read, so this makes them first-class rather than a
 * special case bolted onto a React store.
 *
 * Change notification goes through a window event for the same reason: every copy of this
 * module, in every chunk, hears it.
 */
function globalState(): PrivacyGlobal {
    const existing = (window as unknown as Record<string, PrivacyGlobal | undefined>)[GLOBAL_KEY];

    if (existing) {
        return existing;
    }

    const created: PrivacyGlobal = { enabled: false, revealedClientId: null };
    (window as unknown as Record<string, PrivacyGlobal>)[GLOBAL_KEY] = created;

    return created;
}

function currentEnabled(): boolean {
    return typeof window === 'undefined' ? false : globalState().enabled === true;
}

/**
 * BRO-1567: whether privacy mode is OFFERED to this company. The rollout gate, mirrored
 * from `window.BroadcasterPrivacySeed.available`, which only the dashboard layout writes.
 *
 * `=== false` RATHER THAN `!== true`, AND THE DIRECTION IS DELIBERATE - it is the one
 * place in this file where the "only === true acts" rule would be wrong. Everywhere else
 * the dangerous direction is un-masking, so an unknown must not un-mask. Here the check
 * GUARDS masking, so an unknown must not mask either: a page that never seeded the global
 * (a test harness, a guest layout, a chunk loaded before the seed) would otherwise have
 * every mask silently switched off by this gate. Only an explicit `available: false` from
 * a server render suppresses anything.
 */
function featureUnavailable(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    // BRO-1567 (F1): read from the SEED, which only the dashboard layout writes. This read
    // `window.BroadcasterPrivacy.available`, and was correct only because the object this
    // module manufactures happens to lack that key - so `undefined`, so `=== false` false,
    // so an unseeded page still masked. Safe by an accident nobody would re-check. The
    // gate is a render-time fact about the company, so the seed is where it belongs.
    return readSeed().available === false;
}

function currentRevealed(): number | null {
    return typeof window === 'undefined' ? null : globalState().revealedClientId ?? null;
}

function currentMessagesRevealed(): number | null {
    return typeof window === 'undefined' ? null : globalState().messagesRevealedFor ?? null;
}

/**
 * THE SNAPSHOT IS CACHED, AND THAT IS NOT AN OPTIMISATION.
 *
 * `useSyncExternalStore` calls getSnapshot on every render and compares the result by
 * reference. Building a fresh object each call means it never matches, which React reads
 * as "the store changed again" and re-renders forever. The symptom is a hung tab, not a
 * warning. Rebuilt only when a value actually differs.
 */
/**
 * Seeded from the page: a server-rendered page that masked on the marker cookie has already
 * decided this, and the banner must say it from the first paint rather than after a re-check.
 */
let couldNotConfirm: boolean = seededCouldNotConfirm();

/**
 * The seeded value, read from the page.
 *
 * ONE FUNCTION, used by module load AND by the test reset. They were two copies of the same
 * literal, and a mutation proved the consequence: hard-coding `false` in the module-load copy
 * passed every test, because a test cannot re-trigger module load and only ever observes the
 * reset. Two copies, one of them untestable, is a place for them to drift apart silently -
 * and drifting apart is exactly what produced this bug in the first place.
 */
function seededCouldNotConfirm(): boolean {
    return readSeed().couldNotConfirm === true;
}

/** The render-time seed, or an empty one. Read; never written. */
function readSeed(): { generation?: unknown; couldNotConfirm?: unknown; available?: unknown } {
    const seed = (window as unknown as Record<string, unknown>)[SEED_KEY];

    return (seed && typeof seed === 'object' ? seed : {}) as { generation?: unknown; couldNotConfirm?: unknown; available?: unknown };
}

let snapshot: PrivacySnapshot = {
    enabled: currentEnabled(),
    revealedClientId: currentRevealed(),
    messagesRevealedFor: currentMessagesRevealed(),
    couldNotConfirm,
};

/**
 * Records that the server could not confirm the mode, and notifies if that changed. Kept
 * beside the flag rather than in a component, because the BANNER and the masking must come
 * from one place: a mask held on an unconfirmed basis, with a banner still claiming the
 * server said so, is the overstating banner in its newest costume.
 */
function setCouldNotConfirm(next: boolean): void {
    if (couldNotConfirm === next) {
        return;
    }

    couldNotConfirm = next;
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

const listeners = new Set<() => void>();

/** Rebuild this copy's snapshot from the window state, and tell this copy's subscribers. */
function refresh(): void {
    const enabled = currentEnabled();
    const revealedClientId = currentRevealed();
    const messagesRevealedFor = currentMessagesRevealed();

    // couldNotConfirm is part of the comparison. Without it this returned early whenever only
    // that had changed, so setCouldNotConfirm dispatched the event and every subscriber was
    // handed the identical snapshot back - the banner could never switch to the
    // could-not-confirm wording. A third value was added to the snapshot and the guard that
    // decides whether the snapshot changed was not told about it.
    // BRO-1583 adds a FOURTH value, and the paragraph above is the instruction for
    // doing it: a value added to the snapshot without being added to this guard is a
    // value whose changes never reach a subscriber. Here that would be the Show
    // messages button doing nothing at all when clicked.
    if (
        snapshot.enabled === enabled
        && snapshot.revealedClientId === revealedClientId
        && snapshot.messagesRevealedFor === messagesRevealedFor
        && snapshot.couldNotConfirm === couldNotConfirm
    ) {
        return;
    }

    snapshot = { enabled, revealedClientId, messagesRevealedFor, couldNotConfirm };

    // EACH LISTENER IN ITS OWN TRY, AND THIS IS A SAFETY PROPERTY RATHER THAN TIDINESS.
    //
    // A bare forEach stops at the first listener that throws, so every surface registered
    // after it never hears the change and goes on showing whatever it last rendered. On a
    // privacy store that fails OPEN: real names stay on a screen that now claims to be
    // masked, and nothing says so.
    //
    // Not hypothetical. The calendar's subscriber called a FullCalendar v4 method that no
    // longer exists, threw `rerenderEvents is not a function`, and would have taken every
    // later subscriber down with it.
    //
    // console.error rather than swallowing: this survives into production deliberately,
    // because a surface that cannot re-render is exactly what somebody needs to see.
    listeners.forEach(listener => {
        try {
            listener();
        } catch (error) {
            console.error('[BRO-1567] a privacy-mode subscriber threw; other surfaces still notified', error);
        }
    });
}

if (typeof window !== 'undefined') {
    // Every copy of this module in every chunk registers one of these, so a change made
    // through any copy reaches all of them.
    window.addEventListener(CHANGE_EVENT, refresh);
}

/**
 * BRO-1567: the cross-TAB channel.
 *
 * FOUND BY QUALITY, AND IT IS THE THIRD TRANSITION FAILURE ON THIS TICKET. Switching privacy
 * mode on did not reach tabs that were already open: the switch lives only on Profile, so
 * the ordinary flow is to open Profile in a second tab, switch it on, go back to the inbox
 * tab that was already open, and share that. It showed everything. Browser-proved with
 * controls: tab A on /inbox with the mode switched on in tab B, not reloaded, 4 real names
 * identical to the control and no banner.
 *
 * The other two were the same shape: the calendar rendered masked and would not toggle, and
 * two bundled copies of this module meant a toggle reached one surface and not another.
 * **Every time the steady state was correct and the CHANGE was not** - and the change is the
 * whole feature, because he turns it on and then shares his screen.
 */
const CHANNEL_NAME = 'broadcaster:privacy';

let channel: BroadcastChannel | null = null;

if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);

    channel.onmessage = (event: MessageEvent<{ enabled?: boolean }>) => {
        // APPLIED WITHOUT RE-BROADCASTING. A tab that echoes what it receives turns two tabs
        // into a message loop that never settles.
        //
        // THE ASYMMETRY IS THE WHOLE POINT OF THIS HANDLER. See below: a message may mask
        // this tab on its own authority and may never un-mask it.
        void maskOnMessageUnmaskOnServer(event.data);
    };
}

/**
 * BRO-1567: MASK ON THE MESSAGE, UN-MASK ONLY ON THE SERVER.
 *
 * THE TWO COSTS ARE NOT SYMMETRICAL AND THE CODE MUST NOT TREAT THEM AS IF THEY WERE.
 * Masking on bad information costs a moment of inconvenience: a name is hidden that did not
 * need to be, and one click brings it back. Un-masking on bad information puts a real
 * customer's name in front of a prospect, on a screen that is being shared, and nothing
 * brings that back.
 *
 * So a channel message saying "on" masks immediately and unconditionally. A channel message
 * saying "off" does NOT un-mask: it asks the server, and only the server's answer un-masks.
 *
 * FOUND BY QUALITY. A tab of theirs ended up unmasked while the session said masked, and
 * nothing corrected it, because a hidden background tab receives no `focus` and no
 * `visibilitychange` - so the re-check never ran and a message won indefinitely. This also
 * removes the class of bug they could not rule out, a stale or out-of-order message:
 * ordering guarantees on a BroadcastChannel are not something to reason about at six in the
 * evening.
 *
 * A REVEAL IS NEVER ADOPTED FROM A MESSAGE either, for the same reason - adopting another
 * tab's revealed contact is an un-mask. Any message resets this tab's reveal to null, which
 * is the safe direction and makes decision 1 hold across tabs rather than only within one.
 */
function maskOnMessageUnmaskOnServer(data: { enabled?: boolean } | null | undefined): Promise<void> {
    // ONLY `enabled === true` ACTS DIRECTLY. Everything else - false, missing, malformed, a
    // future message shape this build does not understand - asks the server and changes
    // nothing until it answers.
    //
    // Stated as one rule rather than three because the alternative reads the same and is
    // not: `data?.enabled === true` used to select between "apply true" and "apply false",
    // which quietly made a truncated payload an instruction to UN-MASK. Quality found that
    // one; it is worse than the off case because nothing sent it on purpose.
    /**
     * A MESSAGE CANNOT MASK A TAB WHOSE COMPANY DOES NOT HAVE THE FEATURE.
     *
     * The channel is shared by every tab in the browser and the mode is per session, so a
     * tab belonging to a company that still has privacy mode can broadcast `enabled: true`
     * into one that does not. Masking on that message would leave a screen masked with no
     * banner to explain it and no switch anywhere to clear it - the Profile control is
     * absent for that company, which is the point of the gate.
     *
     * Placed ABOVE the `=== true` branch rather than inside it so it covers the whole
     * handler: with the feature off, the `off` path's re-check is pointless too, because
     * the server can only ever answer off.
     */
    if (featureUnavailable()) {
        return Promise.resolve();
    }

    if (data?.enabled === true) {
        // Already masked: leave this tab alone. Re-applying would clear a contact the
        // operator has deliberately revealed in the tab in front of him, for no gain.
        if (currentEnabled()) {
            return Promise.resolve();
        }

        // BRO-1583: null. Privacy turning on ends any message reveal - this tab is
        // being masked, and a conversation left un-blurred through it is the whole
        // failure the mode exists to prevent.
        applyState(true, null, null);

        return Promise.resolve();
    }

    return recheckFromServer({ force: true });
}

/** Writes the shared state and tells this page. Does NOT touch other tabs. */
/**
 * BRO-1583: messagesRevealedFor is an EXPLICIT third parameter with no default, so every
 * one of the seven callers has to say what it means to happen to the message reveal.
 *
 * A default would have been the bug. Four of those callers are mode changes, where the
 * reveal must END; two are the other axis, where it must SURVIVE. Whichever way a default
 * went, half the callers would silently get the wrong one, and the failure is invisible
 * in both directions: a reveal that outlives privacy being toggled shows a stranger's
 * messages on a shared screen, and one that dies when the operator reveals the contact's
 * name re-blurs a demo mid-sentence.
 */
function applyState(
    enabled: boolean,
    revealedClientId: number | null,
    messagesRevealedFor: number | null
): void {
    const state = globalState();
    state.enabled = enabled;
    state.revealedClientId = revealedClientId;
    state.messagesRevealedFor = messagesRevealedFor;

    // Synchronous and same-window, so the copy that made the change is refreshed by its own
    // listener above rather than by a second code path.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * BRO-1567: THE CHANNEL CARRIES THE MODE ONLY. A REVEAL DOES NOT CROSS TABS.
 *
 * Decided by the Director after Quality questioned it, and it is a refinement of decision 1
 * rather than a new rule.
 *
 * The mode is a shared property of the session. **A reveal is a local, deliberate act about
 * the one contact the operator is looking at in the tab in front of him.** Propagating it
 * would mean an action in a private tab un-masks a contact in the tab he is SHARING - the
 * same failure direction as un-masking on a bad message, and arguably worse, because he
 * took the action himself and has no reason to think it travelled.
 *
 * It would also quietly widen decision 1. "One contact revealed at a time" bounds exposure
 * to exactly one row; session-wide, that bound would span every open tab including ones he
 * cannot see.
 *
 * So the single-reveal invariant is PER TAB, and `broadcast` is false for reveal and hide.
 */
function publish(
    enabled: boolean,
    revealedClientId: number | null,
    messagesRevealedFor: number | null,
    broadcast = true
): void {
    applyState(enabled, revealedClientId, messagesRevealedFor);

    if (broadcast) {
        // The mode, and nothing else. Every other tab, including ones showing a contact list.
        channel?.postMessage({ enabled });
    }
}

/**
 * THE SAFETY NET, AND IT CATCHES WHAT BROADCASTING CANNOT: a broadcast only reaches
 * listeners that were listening. A tab restored from bfcache, one opened before the channel
 * existed, or a second browser profile entirely hears nothing.
 *
 * So a tab re-reads the flag from the SERVER when it regains focus or becomes visible -
 * which is precisely the moment before somebody starts sharing it.
 *
 * Throttled, because switching between two tabs fires this constantly and the answer cannot
 * change without a request that would have broadcast anyway. The throttle is a floor on
 * request volume, not on correctness: the worst case is a second-old answer in a tab
 * nobody has looked at for less than a second.
 */
const RECHECK_THROTTLE_MS = 1000;

let lastRecheck = 0;

/**
 * BRO-1567: the highest generation this tab has seen from the server. Anything lower is an
 * answer to a question asked before the last one and is discarded.
 *
 * THE RACE THIS CLOSES, which is the ordinary path rather than an edge case. The tab
 * re-checks the flag on focus, and the demo flow is alt-tab back from the video call and
 * click straight onto the switch: that fires a GET and a POST at the same moment. If the GET
 * was issued first but its response arrives second, this tab applies the older answer - the
 * operator's click is undone on screen while the server has it right, which is the worst
 * direction for this feature to be wrong in, because the banner and the names then disagree.
 *
 * A counter and not a timestamp: two writes inside one second are indistinguishable by clock,
 * and it is the ORDER being established rather than the time.
 *
 * Seeded from the page, so a response that predates this render is stale too.
 */
let highestGeneration: number = (() => {
    const seeded = readSeed().generation;

    return typeof seeded === 'number' ? seeded : 0;
})();

/**
 * Records a generation and says whether the answer carrying it is still current.
 *
 * A response with NO generation is accepted: an older server, or a shape that changed,
 * must not leave a tab unable to learn anything. It simply cannot be ordered.
 */
function isCurrentGeneration(generation: unknown): boolean {
    if (typeof generation !== 'number') {
        return true;
    }

    if (generation < highestGeneration) {
        return false;
    }

    highestGeneration = generation;

    return true;
}

/**
 * BRO-1567 (F3): IS THIS 404 THE SERVER DELIBERATELY SAYING "PRIVACY MODE IS NOT OFFERED"?
 *
 * ONE function, used by both the focus re-check and the banner's turn-off, so the two cannot
 * come to disagree about what counts - which is how a guard rots.
 *
 * Only a 404 carrying `{"available": false}` counts. PrivacyModeController sends that body and
 * nothing else does. Laravel's own 404 is `{"message":"Not Found"}`, and a stale route cache or
 * a container mid-swap onto a build without the route produces exactly that. So a bare 404,
 * HTML, an empty or malformed body, any other status, or a network failure all return false
 * and leave the tab as it was. UN-MASKING A SHARED SCREEN BECAUSE A DEPLOY WAS IN PROGRESS is
 * the failure this whole feature exists to prevent, so the status code alone is never enough.
 *
 * Reads the body, so call it only on a path that returns afterwards without reading it again.
 */
async function isPrivacyModeWithdrawn(response: Response): Promise<boolean> {
    if (response.status !== 404) {
        return false;
    }

    try {
        const body: unknown = await response.json();

        return typeof body === 'object'
            && body !== null
            && (body as { available?: unknown }).available === false;
    } catch {
        return false;
    }
}

/**
 * BRO-1567 (F3): the company no longer has privacy mode, so this tab stops masking.
 *
 * The same sequence the re-check uses for an authoritative "off": clear the could-not-confirm
 * signal and the seed's copy of it, then applyState rather than setPrivacyEnabled, because this
 * tab is REACTING to a change it did not make and re-broadcasting would bounce it round every
 * other tab. Each of those learns on its own re-check.
 *
 * SILENT, deliberately - the ruling's "say nothing" option. The banner renders nothing once
 * privacy is off, so it simply goes; there is no "did not work" message and no retry advice,
 * because nothing failed. Privacy mode is genuinely off for this account now.
 */
function applyWithdrawal(): void {
    setCouldNotConfirm(false);

    const seed = (window as unknown as Record<string, { couldNotConfirm?: boolean } | undefined>)[SEED_KEY];

    if (seed) {
        seed.couldNotConfirm = false;
    }

    if (currentEnabled()) {
        // BRO-1583: the feature is gone, so every reveal it granted goes with it.
        applyState(false, null, null);
    }
}

async function recheckFromServer({ force = false }: { force?: boolean } = {}): Promise<void> {
    const now = Date.now();

    // `force` is for the un-mask confirmation, which must never be dropped by a throttle
    // meant for focus churn. A tab that swallowed its own confirmation stays masked, which
    // is safe, but it also stays masked after a real change and looks broken.
    if (!force && now - lastRecheck < RECHECK_THROTTLE_MS) {
        return;
    }

    lastRecheck = now;

    try {
        const response = await fetch('/profile/privacy-mode', {
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            // BRO-1567 (F3): a masked open tab whose company had the flag withdrawn used to stay
            // masked here forever. Un-mask ONLY on the deliberate answer; anything else returns
            // as before and cannot un-mask.
            if (await isPrivacyModeWithdrawn(response)) {
                applyWithdrawal();
            }

            return;
        }

        const payload: { enabled?: boolean; known?: boolean; generation?: number } = await response.json();

        const serverSays = payload.enabled === true;

        /**
         * AN UNKNOWN IS ACCEPTED WHATEVER ITS GENERATION, and it has to be.
         *
         * After a cache loss the store answers `known: false` with generation 0 - the counter
         * lives in the entry that was just evicted. Any tab that has seen a real generation
         * holds a higher number, so the ordering guard discarded the very answer that tells it
         * to say "could not be confirmed". The banner could never appear from a re-check.
         *
         * Safe because an unknown can only ever push TOWARDS masked: it never un-masks a tab
         * that is currently masked, which is the branch below. Ordering matters for deciding
         * between two authoritative answers; an unknown is not one of those.
         */
        const unknown = payload.known === false;

        if (!unknown && !isCurrentGeneration(payload.generation)) {
            return;
        }

        /**
         * AN UNKNOWN NEVER UN-MASKS A TAB THAT IS ALREADY MASKED, and it always says so.
         *
         * `known: false` means the store holds no answer for this session - an eviction, a
         * restart, or a deploy running `cache:clear`, any of which can happen mid-share. It
         * is NOT the same as an explicit off.
         *
         * TWO SHAPES OF UNKNOWN, and an earlier version only handled one of them:
         *
         *   unknown + enabled  - masked on this browser's marker cookie. The server says so
         *                        itself. Stay masked, raise the signal.
         *   unknown + disabled - no marker. A tab that is currently masked STILL holds its
         *                        mask, because un-masking on an unknown is the failure this
         *                        feature exists to prevent, and raises the signal too - it is
         *                        now masked on nothing but its own previous state, which is
         *                        exactly the thing the banner must not overstate.
         *
         * Only `known: true` clears the signal. `known` absent - an older server - counts as
         * known, or a tab could never come back at all.
         */
        if (unknown) {
            setCouldNotConfirm(true);

            // Never downwards on an unknown. Upwards is fine: the server is masking on the
            // marker and this tab should follow it.
            if (serverSays && !currentEnabled()) {
                // BRO-1583: masking upwards ends the message reveal too.
                applyState(true, null, null);
            }

            return;
        }

        setCouldNotConfirm(false);

        // The render-time seed is stale the moment the server gives an authoritative answer,
        // so it is cleared rather than left to be misread later. It is only ever consulted at
        // module load, but a seed that outlives the fact it described is exactly the shape
        // this separation exists to prevent.
        const seed = (window as unknown as Record<string, { couldNotConfirm?: boolean } | undefined>)[SEED_KEY];

        if (seed) {
            seed.couldNotConfirm = false;
        }

        if (serverSays !== currentEnabled()) {
            // applyState rather than setPrivacyEnabled: this tab is REACTING to a change it
            // did not make, so re-broadcasting would bounce it back to the tab that sent it
            // and to every other tab that has already handled it.
            // BRO-1583: a mode change from anywhere ends the reveal, the same as one
            // made in this tab.
            applyState(serverSays, null, null);
        }
    } catch {
        // A failed re-check leaves the tab as it was. It cannot un-mask anything, which is
        // the right direction for this to fail in.
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => void recheckFromServer());
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            void recheckFromServer();
        }
    });
    // bfcache: a restored page fires pageshow with persisted=true and no focus event of its
    // own in some browsers, so it is asked explicitly.
    window.addEventListener('pageshow', (event) => {
        if ((event as PageTransitionEvent).persisted) {
            void recheckFromServer();
        }
    });
}

export function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

export function getPrivacySnapshot(): PrivacySnapshot {
    return snapshot;
}

/**
 * Turning privacy mode OFF also drops the revealed contact, so turning it back on never
 * resumes with somebody already exposed. Turning it ON drops it too: whatever was revealed
 * was revealed under the previous state and consenting to it again is one click.
 *
 * This does NOT write the server session. The Profile control and the banner own that,
 * and they call this after the write lands so the page never shows a state the session
 * does not have.
 */
export function setPrivacyEnabled(next: boolean): void {
    if (currentEnabled() === next && currentRevealed() === null) {
        return;
    }

    // BRO-1583: TOGGLING PRIVACY RE-MASKS, which the ticket requires explicitly. Off
    // then on is how an operator resets a shared screen, and it has to mean it.
    publish(next, null, null);
}

/**
 * Decision 1, and it is enforced by the assignment rather than by anybody remembering:
 * there is ONE field on ONE object on `window`, so revealing a second contact re-hides the
 * first. However many times a control is mis-clicked, exactly one row is ever exposed, and
 * a mistake self-corrects instead of accumulating.
 */
export function revealClient(clientId: number | string): void {
    const id = normaliseId(clientId);

    if (id === null || !currentEnabled() || currentRevealed() === id) {
        return;
    }

    // BRO-1583: the message reveal SURVIVES. Revealing the contact's name to compose a
    // message must not silently re-blur the conversation being demonstrated: two axes,
    // neither clearing the other.
    publish(true, id, currentMessagesRevealed());
}

/**
 * BRO-1567: COMPOSING A MESSAGE FROM A CONTACT'S DETAILS REVEALS THAT CONTACT.
 *
 * The Director's ruling 19. Filling a quick reply or a template's parameters puts the
 * contact's real name, number and email into text that is about to be SENT. Masking those
 * values would send "P*** A***" to the customer - the export guardrail's failure arriving
 * through the message box - and masking a preview while the inputs stayed real would be a
 * preview that lies about what is going out.
 *
 * So the deliberate act reveals instead. Editing and composing are the same act.
 *
 * ONE FUNCTION FOR BOTH CALLERS, and that is the point of it existing rather than each
 * caller calling revealClient itself: the chat composer's quick reply and the template
 * picker's parameter fill are the same decision, and two copies of a decision are two places
 * for it to drift. It also makes the decision testable, which a 4,905-line page component
 * does not.
 *
 * The reveal is VISIBLE by construction: it writes the one value the banner and every
 * Show/Hide control read. It is one contact, and it ends when he hides or moves on.
 */
export function revealForComposing(clientId: number | string | null | undefined): void {
    // A missing id reveals NOBODY, and that protection lives in revealClient, which returns
    // early when normaliseId gives null. An extra guard here was written first and removed:
    // mutating it away changed no behaviour and no test could tell the difference, which
    // makes it a defensive layer that reads as load-bearing and is not. The test for the
    // missing-id case stays, pointed at the behaviour rather than at this function's shape.
    revealClient(clientId as number | string);
}

export function hideRevealedClient(): void {
    if (currentRevealed() === null) {
        return;
    }

    // BRO-1583: and hiding the name again does not re-blur the messages either.
    publish(currentEnabled(), null, currentMessagesRevealed(), false);
}

/**
 * BRO-1583: show ONE conversation's message bodies and media, identity still masked.
 *
 * A presenter demonstrating a live account pre-screens a chat, decides it holds nothing
 * personal beyond the identity fields, and wants to show the real flow and the real
 * images with the customer still anonymous.
 *
 * ONE AT A TIME, the same bound as revealClient and for the same reason: revealing a
 * second conversation ends the first. The exposure a presenter has to keep track of is
 * then exactly one chat, not a growing set he cannot see from the screen he is sharing.
 *
 * NOT BROADCAST. Like the contact reveal, this is a local act about the tab in front of
 * him. Propagating it would un-blur a conversation in a tab he is SHARING because of
 * something he did in one he is not.
 *
 * Only while privacy mode is on: with it off there is nothing to reveal, and a reveal
 * recorded against a mode that is off would outlive being turned back on.
 */
export function revealMessagesFor(clientId: number | string): void {
    const id = normaliseId(clientId);

    if (id === null || !currentEnabled() || currentMessagesRevealed() === id) {
        return;
    }

    publish(currentEnabled(), currentRevealed(), id, false);
}

/** End the message reveal. Leaving the conversation and toggling privacy also do this. */
export function hideRevealedMessages(): void {
    if (currentMessagesRevealed() === null) {
        return;
    }

    publish(currentEnabled(), currentRevealed(), null, false);
}

/**
 * Are THIS conversation's bodies and media shown?
 *
 * False whenever privacy mode is off, which is not the same as "not revealed": with the
 * mode off nothing is blurred in the first place, so a caller asking this to decide
 * whether to blur gets the right answer either way, and one asking it to decide whether
 * to show a Hide button gets a button that is correctly absent.
 */
export function areMessagesRevealed(clientId: number | string | null | undefined): boolean {
    const id = normaliseId(clientId);

    return currentEnabled() && id !== null && currentMessagesRevealed() === id;
}

/**
 * BRO-1567: contact ids are TYPED as both numbers and strings in this codebase.
 *
 * `ClientInfoModal`, `ClientInfoInline`, `ClientEditButton` and `AutocorrectButton` all
 * type theirs `number | string | null`. The store keeps one canonical form, so a strict
 * `!==` never compares a string id to a numeric one.
 *
 * CORRECTION, AND IT IS MINE. I first justified this by saying "a Blade mount hands React a
 * string". **That is false.** Every `'clientId' =>` in `resources/views` passes a PHP int -
 * `chat.blade.php:19` casts `(int)` explicitly - and `json_encode` renders an int as a JSON
 * number. I asserted a mechanism that sounded right and never checked it: the same failure
 * as the chat-upload residual, a premise riding inside a plausible case.
 *
 * The normalisation STAYS, because the types permit a string at four call sites and a future
 * caller can supply one, and `Number()` on a number is a no-op. But it is defensive rather
 * than a fix for an observed bug, and whoever reads this next should know which it is.
 *
 * WITHOUT THIS THE FAILURE IS QUIET AND ONE-SIDED: `'7' !== 7` is true, so the contact
 * would be permanently hidden and the reveal button would do nothing at all. It fails
 * closed rather than open, which is the right direction and is exactly why nobody would
 * notice until the operator clicked Show and nothing happened, mid-demo.
 *
 * Found by TypeScript when the modal, whose prop is the union, was wired up.
 */
function normaliseId(clientId: number | string | null | undefined): number | null {
    if (clientId === null || clientId === undefined || clientId === '') {
        return null;
    }

    const asNumber = typeof clientId === 'number' ? clientId : Number(clientId);

    return Number.isFinite(asNumber) ? asNumber : null;
}

/**
 * THE ONE PLACE THAT DECIDES WHETHER A CONTACT IS MASKED.
 *
 * Written once and called by both the plain function and the hook below, because it was
 * briefly written twice: mutating one copy to show unmasked rows left the other copy's
 * tests green, which is a defect that would ship. Two implementations of a predicate whose
 * failure mode is "shows a real name" is not a duplication worth tolerating.
 *
 * Pure, and takes the snapshot rather than reading the module state, so the hook decides
 * from exactly the value React last rendered.
 */
function hiddenIn(current: PrivacySnapshot, clientId: number | string | null | undefined): boolean {
    if (!current.enabled) {
        return false;
    }

    const id = normaliseId(clientId);

    // A row with no id cannot be the revealed one, and failing towards masked is the
    // right way round for a privacy control.
    if (id === null) {
        return true;
    }

    return current.revealedClientId !== id;
}

/** Whether this contact's details must be masked right now. */
export function isClientHidden(clientId: number | string | null | undefined): boolean {
    return hiddenIn(snapshot, clientId);
}

/**
 * Writes the flag to the server session, then updates the store.
 *
 * IN THAT ORDER, AND IT MATTERS IN ONE DIRECTION. Turning privacy ON optimistically would
 * show a masked page whose session still says off, so the next page load un-masks
 * everything with no warning - mid-demo, which is the only time this feature is used. So
 * the page never claims a state the session does not have.
 *
 * The window global is updated too, because Blade, Alpine and the FullCalendar path read
 * that rather than this module, and a toggle that only convinced React would mask half the
 * screen.
 *
 * Returns whether it landed. The caller says so; this does not swallow it.
 */
export async function requestPrivacyMode(next: boolean): Promise<boolean> {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

    try {
        const response = await fetch('/profile/privacy-mode', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrf,
            },
            body: JSON.stringify({ enabled: next }),
        });

        if (!response.ok) {
            // BRO-1567 (F3): turning privacy mode off for a company that no longer has it. That
            // is not a failure - the mode IS off - so it lands, and the banner goes without
            // saying "did not work". A bare 404 or any other refusal still reports failure.
            if (await isPrivacyModeWithdrawn(response)) {
                applyWithdrawal();

                return true;
            }

            return false;
        }

        // Record the write's generation before applying it locally, so an in-flight
        // re-check issued before this POST is recognised as stale when it lands.
        const payload: { generation?: number } = await response.json().catch(() => ({}));

        isCurrentGeneration(payload.generation);
    } catch {
        return false;
    }

    setPrivacyEnabled(next);

    return true;
}

/**
 * Test seam for the channel handler. The real one is assigned to a BroadcastChannel created
 * at module load, which a test cannot reach; this exposes the DECISION it makes, which is
 * the part worth holding.
 */
export function maskOnMessageUnmaskOnServerForTests(data: { enabled?: boolean } | null | undefined): Promise<void> {
    /**
     * DELEGATES. It used to MIRROR the handler's decision - re-implementing the `=== true`
     * branch here and calling recheckFromServer() directly on the other path - and that is
     * a seam that can disagree with the thing it stands for. It already had: the rollout
     * gate added at the top of the real handler was invisible through this function, so a
     * test asserting "no server re-check for an unavailable company" failed against code
     * that was correct, and any FUTURE change to the off path would have been untested in
     * the same silent way.
     *
     * The handler now returns its own promise, so awaiting this really does wait for the
     * round trip - which is the property the mirrored version was written to get, and the
     * only reason it existed.
     */
    return maskOnMessageUnmaskOnServer(data);
}

/** Test seam. Nothing in the app calls this. */
/**
 * Test seam for the focus re-check. The real caller is a `focus` listener registered at
 * module load, which a test cannot fire meaningfully; this exposes the request itself, which
 * is the part that carries the staleness decision.
 */
export function recheckFromServerForTests(options: { force?: boolean } = {}): Promise<void> {
    return recheckFromServer(options);
}

export function resetPrivacyStoreForTests(): void {
    const state = globalState();
    state.revealedClientId = null;
    // BRO-1583: the new axis is reset here too. A reveal surviving into the next test is
    // the kind of leak that makes one test pass because of what another one did.
    state.messagesRevealedFor = null;
    state.enabled = state.enabled === true;

    // BRO-1567: module-level, so without this a test that pushes the generation up leaves
    // every later test's server answers looking stale and silently ignored.
    highestGeneration = 0;
    lastRecheck = 0;

    // RE-SEEDED FROM THE PAGE, not forced to false, because that is what module load does.
    // Forcing false made a test that set the page seed unable to observe it, which is the
    // reset quietly disagreeing with the thing it resets.
    //
    // BEFORE the snapshot is built, not after: building it first captured the OLD value and
    // the re-seed then had nothing to do - the same ordering slip as the three bugs this
    // reset exists to let tests catch.
    couldNotConfirm = seededCouldNotConfirm();

    snapshot = {
        enabled: currentEnabled(),
        revealedClientId: null,
        messagesRevealedFor: null,
        couldNotConfirm,
    };
    listeners.clear();
}

export function usePrivacyMode(): PrivacySnapshot {
    return useSyncExternalStore(subscribe, getPrivacySnapshot, getPrivacySnapshot);
}

/**
 * The predicate every surface needs: "do I mask this contact?".
 *
 * Deliberately a hook over the store rather than a prop threaded down, because the
 * alternative is every list passing `hidden` through three components and one of them
 * forgetting. A surface that forgets to call this shows a real name, so the cheapest
 * possible call site is the point.
 */
export function useIsClientHidden(clientId: number | string | null | undefined): boolean {
    return hiddenIn(usePrivacyMode(), clientId);
}

/**
 * BRO-1583: is this conversation's content revealed?
 *
 * SUBSCRIBED, like its neighbour, and for the reason this feature has relearned three
 * times: reading the flag once at render and trusting it is right in the steady state and
 * wrong at exactly the moment that matters. The button's own click is that moment.
 *
 * Derived from the snapshot rather than calling areMessagesRevealed(), so the value React
 * renders and the value the store holds cannot disagree.
 */
export function useAreMessagesRevealed(clientId: number | string | null | undefined): boolean {
    const snapshot = usePrivacyMode();
    const id = normaliseId(clientId);

    return snapshot.enabled && id !== null && snapshot.messagesRevealedFor === id;
}
